import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { AppThunk } from 'src/store';
import * as uuid from 'uuid';
import {
  ICommunicationRequest,
  ITaskDetails,
  ERoleName,
  TaskDetailStatusReason,
  TaskDetailWith,
  TaskStatus
} from '@actimi/core-migration';
import getActimiCoreLazy from '../utils/actimi-core';

interface ICommunicationRequestState {
  requests: ICommunicationRequest[];
  filteredRequests: ICommunicationRequest[];
  taskDetails?: ITaskDetails[];
  isTasksLoading?: boolean;
}

const initialState: ICommunicationRequestState = {
  requests: [],
  filteredRequests: [],
  taskDetails: [],
  isTasksLoading: true
};

const slice = createSlice({
  name: 'communication-request',
  initialState,
  reducers: {
    communicationRequestChanged(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        requests: ICommunicationRequest[];
      }>
    ) {
      const { requests } = action.payload;
      requests.forEach((x) => {
        const index = state.requests.findIndex((y) => y.id === x.id);
        const filterIndex = state.filteredRequests.findIndex(
          (y) => y.id === x.id
        );
        if (index > -1) {
          state.requests.splice(index, 1, x);
        } else {
          state.requests.push(x);
        }
        if (filterIndex > -1) {
          if (
            state.filteredRequests[filterIndex].recipient.length !==
            x.recipient.length
          ) {
            state.filteredRequests.splice(filterIndex, 1);
          } else {
            state.filteredRequests.splice(index, 1, x);
          }
        }
      });
    },
    changeTaskDetailStatus(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        id: string;
        status: TaskStatus;
      }>
    ) {
      const { status, id } = action.payload;
      const index = state.taskDetails.findIndex((x) => x.id === id);
      state.taskDetails[index].status = status;
    },
    addTaskDetailNote(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        id: string;
        comment: string;
        userId: string;
        role: string;
      }>
    ) {
      const { id, comment, userId, role } = action.payload;
      const index = state.taskDetails.findIndex((x) => x.id === id);
      if (index > -1) {
        state.taskDetails[index].comments.push({
          id,
          comment,
          commenterId: userId,
          role,
          dateTime: new Date().toISOString()
        });
      }
    },
    getCommunicationRequest(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        requests: ICommunicationRequest[];
      }>
    ) {
      const { requests } = action.payload;
      requests.forEach((x) => {
        const index = state.requests.findIndex((y) => y.id === x.id);
        if (index > -1) {
          state.requests.splice(index, 1, x);
        } else {
          state.requests.push(x);
        }
      });
    },
    getTaskDetails(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        tasks: ITaskDetails[];
      }>
    ) {
      const { tasks } = action.payload;

      if (tasks !== undefined) {
        state.isTasksLoading = false;
      }

      tasks.forEach((x) => {
        const index = state.taskDetails.findIndex((y) => y.id === x.id);
        if (index > -1) {
          state.taskDetails.splice(index, 1, x);
        } else {
          state.taskDetails.push(x);
        }
      });
    },
    addCommunicationRequestRecipient(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        id: string;
        recipient: string;
      }>
    ) {
      const { recipient, id } = action.payload;
      const index = state.requests.findIndex((y) => y.id === id);
      const filteredIndex = state.filteredRequests.findIndex(
        (y) => y.id === id
      );
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const [resourceType, _id] = recipient.split('/');
      // @ts-ignore
      state.requests[index].recipient.push({ resourceType, id: _id });
      if (filteredIndex > -1) {
        state.filteredRequests.splice(index, 1, {
          ...state.requests[index]
        });
      }
      if (index > -1) {
        state.requests.splice(index, 1, {
          ...state.requests[index]
        });
      } else {
        state.requests.push(state.requests[index]);
      }
    },
    getFilteredCommunicationRequest(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        subjectId?: string;
        status?: 'draft' | 'active' | 'completed' | string;
        recipient: string | string[];
      }>
    ) {
      const { subjectId, recipient, status } = action.payload;
      if (status !== undefined && status.toLowerCase() === 'draft') {
        state.filteredRequests = state.requests.filter(
          (x) => x.recipient.length === 1
        );
      } else {
        state.filteredRequests = state.requests.filter(
          (x) =>
            (subjectId !== undefined && x.subject.id === subjectId) ||
            (x.recipient.some((y) =>
              Array.isArray(recipient)
                ? recipient.find((w) => {
                    return y.id === w.split('/')[1];
                  }) !== undefined
                : y.id === recipient.split('/')[1]
            ) && status !== undefined
              ? x.status.toLowerCase() === status.toLowerCase()
              : x.status !== undefined)
        );
      }
    },
    changeCommunicationRequestNote(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        id: string;
        note: string;
      }>
    ) {
      const { note, id } = action.payload;
      const requestIndex = state.requests.findIndex((x) => x.id === id);
      const filteredRequestIndex = state.filteredRequests.findIndex(
        (x) => x.id === id
      );
      if (requestIndex > -1) {
        state.requests.splice(requestIndex, 1, {
          ...state.requests[requestIndex],
          note: [
            {
              text: note,
              author: {
                string:
                  state.requests[requestIndex].note !== undefined
                    ? state.requests[requestIndex].note[0].author.string
                    : 'Actimi GmbH'
              },
              time: new Date().toISOString()
            }
          ]
        });
      }
      if (filteredRequestIndex > -1) {
        state.filteredRequests.splice(filteredRequestIndex, 1, {
          ...state.requests[requestIndex],
          note: [
            {
              text: note,
              author: {
                string:
                  state.requests[requestIndex].note !== undefined
                    ? state.requests[requestIndex].note[0].author.string
                    : 'Actimi GmbH'
              },
              time: new Date().toISOString()
            }
          ]
        });
      }
    },
    addCommunicationRequestNote(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        id: string;
        note: string;
        author: string;
        role: string;
      }>
    ) {
      const { note, id, author, role } = action.payload;
      const requestIndex = state.requests.findIndex((x) => x.id === id);
      const filteredRequestIndex = state.filteredRequests.findIndex(
        (x) => x.id === id
      );
      const taskDetailIndex = state.taskDetails.findIndex((x) => x.id === id);
      if (requestIndex > -1) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        state.requests[requestIndex].note === undefined
          ? (state.requests[requestIndex].note = [
              {
                text: note,
                author: {
                  string: author
                },
                time: new Date().toISOString()
              }
            ])
          : state.requests[requestIndex].note.push({
              text: note,
              author: {
                string: author
              },
              time: new Date().toISOString()
            });
      }
      if (filteredRequestIndex > -1) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        state.requests[requestIndex].note === undefined
          ? (state.requests[requestIndex].note = [
              {
                text: note,
                author: {
                  string: author
                },
                time: new Date().toISOString()
              }
            ])
          : state.requests[requestIndex].note.push({
              text: note,
              author: {
                string: author
              },
              time: new Date().toISOString()
            });
      }
      if (taskDetailIndex > -1) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        state.taskDetails[taskDetailIndex].comments === undefined
          ? (state.taskDetails[taskDetailIndex].comments = [
              {
                id: uuid.v4(),
                commenterId: author,
                role,
                comment: note,
                dateTime: new Date().toISOString()
              }
            ])
          : state.taskDetails[taskDetailIndex].comments.push({
              id: uuid.v4(),
              commenterId: author,
              role,
              comment: note,
              dateTime: new Date().toISOString()
            });
      }
    },

    changeCommunicationRequestCategory(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        id: string;
        category: string;
      }>
    ) {
      const { category, id } = action.payload;
      const requestIndex = state.requests.findIndex((x) => x.id === id);
      const filteredRequestIndex = state.filteredRequests.findIndex(
        (x) => x.id === id
      );
      if (requestIndex > -1) {
        state.requests.splice(requestIndex, 1, {
          ...state.requests[requestIndex],
          category: [{ coding: [{ code: category.toLowerCase() }] }]
        });
      }
      if (filteredRequestIndex > -1) {
        state.filteredRequests.splice(filteredRequestIndex, 1, {
          ...state.requests[requestIndex],
          category: [{ coding: [{ code: category.toLowerCase() }] }]
        });
      }
    },
    changeCommunicationRequestStatus(
      state: ICommunicationRequestState,
      action: PayloadAction<{
        id: string;
        status: string;
        statusReason: string;
      }>
    ) {
      const { id, status, statusReason } = action.payload;
      const requestIndex = state.requests.findIndex((x) => x.id === id);
      const filteredRequestIndex = state.filteredRequests.findIndex(
        (x) => x.id === id
      );
      if (requestIndex > -1) {
        state.requests.splice(requestIndex, 1, {
          ...state.requests[requestIndex],
          status: status.toLowerCase(),
          statusReason: { coding: [{ code: statusReason.toLowerCase() }] }
        });
      }
      if (filteredRequestIndex > -1) {
        state.filteredRequests.splice(filteredRequestIndex, 1, {
          ...state.requests[requestIndex],
          status: status.toLowerCase(),
          statusReason: { coding: [{ code: statusReason.toLowerCase() }] }
        });
      }
    },
    reset(state: ICommunicationRequestState) {
      Object.assign(state, initialState);
    }
  }
});

export const { reducer } = slice;

export const reset = (): AppThunk => async (dispatch) =>
  dispatch(slice.actions.reset());

export const getTaskDetails =
  (
    organizationId: string,
    statusReason?: TaskDetailStatusReason,
    count?: number,
    sort?: string,
    _with: TaskDetailWith[] = [
      TaskDetailWith.Observation,
      TaskDetailWith.ObservationHasMember,
      TaskDetailWith.QuestionnaireResponse
    ],
    subject?: string,
    recipient?: string,
    status?: string[]
  ): AppThunk =>
  async (dispatch) => {
    const { CommunicationRequestService } = await getActimiCoreLazy();

    const { getTaskDetails: getTaskDetailsAPI } =
      new CommunicationRequestService();

    const tasks = await getTaskDetailsAPI(organizationId, {
      statusReason,
      count,
      sort,
      _with,
      subject,
      recipient,
      status
    });

    dispatch(slice.actions.getTaskDetails({ tasks }));
  };

// export const getCommunicationRequest = (
//     organizationId: string,
//     recipient: string | string[],
//     status?: 'draft' | 'active' | 'completed',
//     subjectId?: string,
//     whenDone?: () => void
//   ): AppThunk =>
//   async (dispatch) => {
//     const { CommunicationRequestService } = await getActimiCoreLazy();

//     const { getCommunicationRequest: getCommunicationRequestAPI } =
//       new CommunicationRequestService();

//     const requests = await getCommunicationRequestAPI(organizationId, {
//       recipient: recipient as string,
//       subject: subjectId,
//       status
//     });

//     dispatch(slice.actions.getCommunicationRequest({ requests }));
//     dispatch(
//       slice.actions.getFilteredCommunicationRequest({
//         recipient,
//         status: 'draft',
//         subjectId
//       })
//     );
//     if (whenDone) {
//       whenDone();
//     }
//   };

export const getFilteredCommunicationRequest =
  (
    recipient: string | string[],
    status: 'draft' | 'active' | 'completed',
    subjectId?: string,
    whenDone?: () => void
  ): AppThunk =>
  async (dispatch) => {
    dispatch(
      slice.actions.getFilteredCommunicationRequest({
        recipient,
        subjectId,
        status
      })
    );
    if (whenDone) {
      whenDone();
    }
  };

export const addCommunicationRequestRecipient =
  (recipient: string, id: string, organizationId: string): AppThunk =>
  async (dispatch) => {
    const { CommunicationRequestService } = await getActimiCoreLazy();

    const {
      addCommunicationRequestRecipient: addCommunicationRequestRecipientAPI
    } = new CommunicationRequestService();

    await addCommunicationRequestRecipientAPI(id, recipient, organizationId);
    dispatch(
      slice.actions.addCommunicationRequestRecipient({
        recipient,
        id
      })
    );
  };

export const changeCommunicationRequestNote =
  (id: string, note: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.changeCommunicationRequestNote({ id, note }));
  };

export const addCommunicationRequestNote =
  (
    id: string,
    note: string,
    author: string,
    role: string,
    whenDone?: () => void
  ): AppThunk =>
  async (dispatch) => {
    dispatch(
      slice.actions.addCommunicationRequestNote({ id, note, author, role })
    );
    if (whenDone) {
      whenDone();
    }
  };

export const changeCommunicationRequestCategory =
  (id: string, category: string): AppThunk =>
  async (dispatch) => {
    dispatch(
      slice.actions.changeCommunicationRequestCategory({ id, category })
    );
  };

export const changeCommunicationRequestStatus =
  (
    id: string,
    status: string,
    statusReason: string,
    whenDone?: () => void
  ): AppThunk =>
  async (dispatch) => {
    dispatch(
      slice.actions.changeCommunicationRequestStatus({
        id,
        status,
        statusReason
      })
    );
    if (whenDone) {
      whenDone();
    }
  };

export const communicationRequestChanged =
  (communicationRequest: ICommunicationRequest): AppThunk =>
  async (dispatch) => {
    dispatch(
      slice.actions.communicationRequestChanged({
        requests: [communicationRequest]
      })
    );
  };

export const addTaskDetailNote =
  (
    id: string,
    comment: string,
    userId: string,
    role: string,
    organizationId: string
  ): AppThunk =>
  async (dispatch) => {
    const { CommunicationRequestService } = await getActimiCoreLazy();

    const { addTaskDetailNote: addTaskDetailNoteAPI } =
      new CommunicationRequestService();

    addTaskDetailNoteAPI(
      id,
      {
        comment,
        dateTime: new Date().toISOString(),
        commenterId: userId,
        id,
        role
      },
      organizationId
    );

    dispatch(
      slice.actions.addTaskDetailNote({
        id,
        comment,
        userId,
        role
      })
    );
  };

export const updateCommunicationRequest =
  (
    communicationRequest: ICommunicationRequest,
    status: string,
    organizationId: string
  ): AppThunk =>
  async (dispatch) => {
    const { CommunicationRequestService } = await getActimiCoreLazy();

    const { updateCommunicationRequest: updateCommunicationRequestAPI } =
      new CommunicationRequestService();

    const data = await updateCommunicationRequestAPI(
      organizationId,
      communicationRequest
    );

    dispatch(slice.actions.getCommunicationRequest({ requests: [data] }));
    dispatch(
      slice.actions.getFilteredCommunicationRequest({
        status,
        recipient: communicationRequest.recipient.map(
          (x: { resourceType: string; id: string }) =>
            `${x.resourceType}/${x.id}`
        )
      })
    );
  };

export const changeTaskDetailStatus =
  (
    id: string,
    userId: string,
    role: ERoleName,
    comment: string,
    status: TaskStatus,
    organizationId: string
  ): AppThunk =>
  async (dispatch) => {
    const { CommunicationRequestService } = await getActimiCoreLazy();

    const {
      addTaskDetailNote: addTaskDetailNoteActimiCore,
      changeTaskDetailStatus: changeTaskDetailStatusAPI
    } = new CommunicationRequestService();

    await addTaskDetailNoteActimiCore(
      id,
      {
        comment,
        dateTime: new Date().toISOString(),
        commenterId: userId,
        id,
        role
      },
      organizationId
    );

    dispatch(
      slice.actions.addTaskDetailNote({
        id,
        comment,
        userId,
        role
      })
    );
    await changeTaskDetailStatusAPI(id, status, organizationId);
    dispatch(
      slice.actions.changeTaskDetailStatus({
        id,
        status
      })
    );
  };

export const completeCommunicationRequest =
  (
    communicationRequest: ICommunicationRequest,
    status: string,
    roles: ERoleName,
    organizationId: string
  ): AppThunk =>
  async (dispatch) => {
    const { CommunicationRequestService } = await getActimiCoreLazy();

    const { updateCommunicationRequest: updateCommunicationRequestAPI } =
      new CommunicationRequestService();

    let data;
    if (roles === ERoleName.Physician) {
      data = updateCommunicationRequestAPI(organizationId, {
        ...communicationRequest,
        status: 'completed'
      });
    } else if (roles === ERoleName.Nurse) {
      data = updateCommunicationRequestAPI(organizationId, {
        ...communicationRequest,
        status: 'doctor'
      });
    }
    dispatch(slice.actions.getCommunicationRequest({ requests: [data] }));
    dispatch(
      slice.actions.getFilteredCommunicationRequest({
        status,
        recipient: communicationRequest.recipient.map(
          (x: { resourceType: string; id: string }) =>
            `${x.resourceType}/${x.id}`
        )
      })
    );
  };

export default slice;
